import React, { Component } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { GoogleMap, Circle, Polygon } from '@react-google-maps/api';
import {
  LocationDto,
  OrderDto,
  PlantDto,
  UrlKeyDto,
  GeoZoneDto,
  RouteDto,
  StatusEventDto,
  PositionDto,
  ProductLine,
  UserRegionDto,
} from '@trucktrax/trucktrax-ts-common';
import { postGISToGoogleMapsPoints, vars } from '@trucktrax/trucktrax-common';

import { connect } from 'react-redux';

import { DefaultLocation } from './mapTypes';
import * as constants from '../../../constants/mapConstants';
import RecenterMapBtn from './RecenterMapBtn';
import icons from './icons';
import { PinLocationInfo } from '../../../store/actions/mapviewActions';
import { DEFAULT_RADIUS, DROP_PIN_ICON, METERS_PER_MILE } from '../../../constants/mapConstants';

import { MapMarkerType, ZoneType } from '../../../types';
import { GEOZONE_TYPE, GEOZONE_SHAPE_STYLE } from '../../../constants/appConstants';
import LegendMapKey from './LegendMapKey';
import { retrieveProductLineNameDiff } from '../../../util/statusesUtil';
import { ReduxState } from '../../../store';
import styles from './RouteHistoryTTMap.module.css';
import TTMarker from './markers/TTMarker';
import PositionMarker from './markers/PositionMarker';
import PlantMarkerWithInfoPane from './markers/PlantMarkerWithInfoPane';
import markerColors from './markers/MarkerColors';
import {
  Z_INDEX_MARKER_TOP,
  Z_INDEX_MARKER_MIDDLE,
  Z_INDEX_MARKER_BOTTOM,
  Z_INDEX_PIN_MARKER_DEFAULT,
} from './markers/markerConstants';
import { determineRegionId, noop } from '../../../util/appUtil';
import { filterActiveMarkersByRegion, PositionMarkerDataInfo } from '../../../util/markerUtil';
import { createIconElement } from './markers/iconUtils';
import { isRouteHistoryMarkerOpen, PopupToggleMap } from '../../../store/reducers/mapReducers';
import { CustomDrawingManager, useAdvancedMarker } from './TraxMapDrawingManager';

import { isValidLatLng, mapsToLocationDto } from '../../../util/geoUtil';

interface MapChangeEvent {
  location?: LocationDto;
  circle?: {
    center: LocationDto;
    radius: number;
  };
  polygon?: {
    points: LocationDto[];
  };
}

type ShapeStyles = {
  fillColor: string;
  strokeColor: string;
  fillOpacity: number;
  strokeWeight: number;
  clickable: boolean;
  zIndex: number;
  draggable: boolean;
  editable: boolean;
};

type routeDataDto = {
  route: RouteDto;
  markerIdx: number;
};

// Advanced Marker wrapper for pin functionality
interface PinMarkerProps {
  map?: google.maps.Map;
  position?: google.maps.LatLngLiteral;
  visible?: boolean;
}

const PinMarker: React.FC<PinMarkerProps> = ({ map, position, visible = true }) => {
  // Create custom pin content
  const pinContent = React.useMemo(() => {
    const div = document.createElement('div');
    const iconConfig = {
      path: icons.pinPath,
      fillColor: vars.blue,
      strokeColor: vars.white,
      strokeWeight: 1,
      scale: 1,
    };
    const iconElement = createIconElement(iconConfig, 1);
    if (iconElement) {
      div.appendChild(iconElement);
    }
    return div;
  }, []);

  useAdvancedMarker({
    map,
    position: position || { lat: 0, lng: 0 },
    content: pinContent,
    visible: visible && !!position,
    draggable: false,
    zIndex: Z_INDEX_PIN_MARKER_DEFAULT,
  });

  return null;
};

export class RouteHistoryTTMap extends Component<RouteHistoryTTMapProps, RouteHistoryTTMapState> {
  circleRef?: google.maps.Circle;

  defaultZoom = 9;

  listenersRef?: google.maps.MapsEventListener[];

  map?: google.maps.Map;

  polygonRef?: google.maps.Polygon;

  // Circle for the pin selection
  pinCircle: google.maps.Circle | null = null;

  // Pin marker position for AdvancedMarkerElement
  pinMarkerPosition: google.maps.LatLngLiteral | null = null;

  gmapControlPositions = google.maps.ControlPosition;

  mapPositions = {
    leftBottom: this.gmapControlPositions.LEFT_BOTTOM,
    rightBottom: this.gmapControlPositions.RIGHT_BOTTOM,
    topRight: this.gmapControlPositions.TOP_RIGHT,
  };

  private recenterButtonRoot?: Root;

  private legendRoot?: Root;

  get hasRouteHistoryProductLines() {
    return this.props.isRouteHistory && (this.props.routeHistoryProductLines ?? []).length > 0;
  }

  constructor(props: RouteHistoryTTMapProps) {
    super(props);
    this.state = {
      isFirstMapRender: true,
      isMapHasBeenBounded: false,
    };
  }

  /** brings last selected to top and the rest of selected above unselected */
  static calculateZIndex = (
    lastMarkerSelected: string | null,
    truckId: string,
    isSelected: boolean,
    maxIdx: number,
  ) => {
    const [top, middle, bottom] = [
      Z_INDEX_MARKER_TOP + maxIdx,
      Z_INDEX_MARKER_MIDDLE + maxIdx,
      Z_INDEX_MARKER_BOTTOM,
    ];
    if (lastMarkerSelected && lastMarkerSelected === truckId && isSelected) {
      return top;
    }
    return isSelected ? middle : bottom;
  };

  componentDidMount() {
    this.reCenterMap();
  }

  componentDidUpdate(prevProps: RouteHistoryTTMapProps) {
    const { markers, currentRegion, routeHistoryProductLines, routeHistoryList, plantList } =
      this.props;

    // If the user explicitly removed the pin, clear the overlays.
    if (this.props.activePinMap?.removePin === true && prevProps.activePinMap?.removePin !== true) {
      this.clearPinOverlays();
    }

    if (plantList && prevProps.plantList && plantList.length !== prevProps.plantList.length) {
      // due to a poorly understood race condition bug
      // we need to force an update here or else the Plant Marker Tooltips will often render offscreen
      // specifically, the InfoBox component will apply "left" and "bottom" properties
      // which place them tens of thousands of pixels offscreen
      this.forceUpdate();
    }

    const currentRouteTimes = routeHistoryList?.map(r => r.route?.[0]?.createdTimestamp ?? '0');
    const priorRouteTimes = prevProps.routeHistoryList?.map(
      r => r.route?.[0]?.createdTimestamp ?? '0',
    );
    const routeTimesChanged =
      currentRouteTimes?.length !== priorRouteTimes?.length ||
      currentRouteTimes?.some((time, index) => time !== priorRouteTimes?.[index]);
    if (routeHistoryList && routeHistoryList.length > 0 && routeTimesChanged) {
      this.map?.setZoom(12);
    }
    if (
      markers?.length !== prevProps.markers?.length ||
      currentRegion?.url !== prevProps.currentRegion?.url
    ) {
      this.reCenterMap();
    }

    // add history legend updates
    if (routeHistoryProductLines !== prevProps.routeHistoryProductLines) {
      this.renderHistoryLegend();
    }

    // check the if the pin circle size changed
    if (this.props.isRouteHistory && this.pinCircle) {
      const storePinRadiusMeters = this.getPinRadiusMilesNumber() * METERS_PER_MILE;
      const currentPinRadius = this.pinCircle.getRadius();
      if (storePinRadiusMeters !== currentPinRadius) {
        this.pinCircle.setRadius(storePinRadiusMeters);
      }
    }
  }

  private getPinRadiusMilesNumber = () => {
    const miles = Number(this.props.pinRadiusMiles);
    return Number.isFinite(miles) ? miles : DEFAULT_RADIUS;
  };

  clearPinOverlays = () => {
    this.pinCircle = null;
    this.pinMarkerPosition = null;

    // Force re-render so React overlays disappear immediately.
    this.forceUpdate();
  };

  componentWillUnmount() {
    if (this.recenterButtonRoot) {
      this.recenterButtonRoot.unmount();
    }
    if (this.legendRoot) {
      this.legendRoot.unmount();
    }
  }

  onPinCircleLoad = (circle: google.maps.Circle) => {
    this.pinCircle = circle;
  };

  onPinCircleUnmount = () => {
    this.pinCircle = null;
  };

  renderPlants = () =>
    this.props.plantList &&
    this.props.plantList.map(plant => {
      // the chosen color will be determined based on plant id, so that plants show up with a stable consistent color
      const colorIndex = plant.id % markerColors.length;
      return (
        <PlantMarkerWithInfoPane
          key={plant.id}
          plant={plant}
          mapRef={this.map}
          markerColor={markerColors[colorIndex]}
          updateToFieldValueWithPlantDrivers={noop}
          onClick={noop}
        />
      );
    });

  onLoad = (map: google.maps.Map) => {
    this.map = map;
    this.renderHistoryLegend();
    this.renderRecenterMapButton();
  };

  renderRecenterMapButton = () => {
    if (!this.map) return;
    const controlButtonDiv = document.createElement('div');
    this.recenterButtonRoot = createRoot(controlButtonDiv);
    this.recenterButtonRoot.render(<RecenterMapBtn onClick={this.reCenterMap} />);
    this.map.controls[this.mapPositions.rightBottom].push(controlButtonDiv);
  };

  renderHistoryLegend = () => {
    if (!this.map) return;
    const { routeHistoryProductLines } = this.props;
    if (!this.hasRouteHistoryProductLines) {
      this.map.controls[this.mapPositions.leftBottom].clear();
      return;
    }
    // gets the Legend Map Key component
    const controlButtonDiv = document.createElement('div');
    this.legendRoot = createRoot(controlButtonDiv);
    this.legendRoot.render(<LegendMapKey productLines={routeHistoryProductLines!} />);

    this.map.controls[this.mapPositions.leftBottom].clear();
    this.map.controls[this.mapPositions.leftBottom].push(controlButtonDiv);
  };

  zoomToBounds = () => {
    let boundsNumber = 0;
    const bounds = new google.maps.LatLngBounds();

    const extendBounds = (lat?: number, lng?: number): number => {
      if (!isValidLatLng(lat, lng)) return 0;
      // At this point, lat and lng are guaranteed to be numbers
      const point = new google.maps.LatLng(lat as number, lng as number);
      bounds.extend(point);
      return 1;
    };

    const { markers, currentRegion } = this.props;

    if (!this.state.isFirstMapRender) return;

    const validMarkerList = filterActiveMarkersByRegion(markers ?? [], currentRegion.url ?? '');

    // This code shows the map center somehow
    const position =
      validMarkerList.length === 1 ? validMarkerList[0].location : currentRegion.location;
    const { latitude, longitude } = position || { latitude: 0, longitude: 0 };
    boundsNumber += extendBounds(latitude, longitude);
    // no need to increment boundsNumber a second time
    if (latitude !== undefined && longitude !== undefined) {
      extendBounds(latitude + 0.0003, longitude + 0.0003);
    }

    if (this.map) {
      if (boundsNumber <= 1) {
        const center = bounds.getCenter();
        // Validate center coordinates before using them
        if (center && isValidLatLng(center.lat(), center.lng())) {
          this.map.setCenter(center);
          this.map.setZoom(this.defaultZoom);
        }
      } else {
        this.map.fitBounds(bounds);
      }
      this.setState({ isFirstMapRender: false });
    }
  };

  reCenterMap = () => {
    this.setState({ isFirstMapRender: true }, this.zoomToBounds);
  };

  handleCenterChange = () => {
    if (this.map && this.map.getCenter && this.props.onCenterChange) {
      this.props.onCenterChange(JSON.stringify(this.map.getCenter()?.toJSON()));
    }
  };

  prevChange = {};

  onChange(change: MapChangeEvent) {
    if (!this.map) return;
    if (JSON.stringify(this.prevChange) === JSON.stringify(change)) {
      return;
    }

    this.prevChange = change;
    if (this.props.onChange !== undefined) {
      this.props.onChange(change);
    }
  }

  onCircleLoad = (circle: google.maps.Circle) => {
    this.circleRef = circle;
    if (!this.state.isMapHasBeenBounded) {
      this.setState({ isMapHasBeenBounded: true }, () => this.zoomToBoundsCP());
    }
  };

  onCircleUnmount = () => {
    this.listenersRef = [];
    this.circleRef = undefined;
  };

  onCircleChanged = () => {
    if (!this.map || !this.circleRef) return;
    this.onCircleComplete(this.circleRef);
  };

  onCircleComplete = (circle?: google.maps.Circle) => {
    if (!this.map || !circle) return;
    const center = mapsToLocationDto(circle.getCenter());
    this.onChange({
      circle: {
        center,
        radius: circle.getRadius(),
      },
      polygon: undefined,
    });
  };

  renderGeozoneCircle = (shapeStyles: ShapeStyles) => {
    const zone = this.props.currentGeozone?.zone;
    if (!zone?.circle?.center?.latitude || !zone?.circle?.center?.longitude) {
      return null;
    }

    return (
      <Circle
        center={{
          lat: zone.circle.center.latitude,
          lng: zone.circle.center.longitude,
        }}
        onLoad={this.onCircleLoad}
        radius={zone.circle.radius}
        onRadiusChanged={this.onCircleChanged}
        onCenterChanged={this.onCircleChanged}
        onUnmount={this.onCircleUnmount}
        options={shapeStyles}
      />
    );
  };

  onPolygonChanged = () => {
    if (!this.polygonRef) return;
    this.onPolygonComplete(this.polygonRef);
  };

  // Clean up refs
  onPolygonUnmount = () => {
    if (this.listenersRef) {
      this.listenersRef.forEach(lis => lis.remove());
    }
    this.listenersRef = [];
    this.polygonRef = undefined;
  };

  onPolygonComplete = (polygon: google.maps.Polygon) => {
    const newPolygon = polygon
      .getPath()
      .getArray()
      .map(point => ({
        lat: point.lat(),
        lng: point.lng(),
      }));
    const points = RouteHistoryTTMap.googleMapsToPostGISPoints(newPolygon) ?? [];
    this.onChange({
      circle: undefined,
      polygon: {
        points,
      },
    });
  };

  static googleMapsToPostGISPoints = (points?: google.maps.LatLngLiteral[]) => {
    if (!points) return null;

    const closedPoints = [...points];
    closedPoints.push(points[0]);
    return closedPoints.map(point => ({
      latitude: point.lat,
      longitude: point.lng,
    }));
  };

  getGeofenceStyle(zoneType?: ZoneType) {
    const shapeStyles: ShapeStyles = {
      fillColor: '#67B4F6',
      strokeColor: '#67B4F6',
      fillOpacity: 0.25,
      strokeWeight: 2,
      clickable: false,
      zIndex: 1,
      draggable: true,
      editable: this.props.isEditable ?? false,
    };

    type GeozoneType = typeof GEOZONE_SHAPE_STYLE;
    type GeozoneShapeStyle = {
      fillColor: string;
      strokeColor: string;
      zIndex: number;
    };

    const zone: { [geozoneName: string]: GeozoneShapeStyle } = {};

    zone[GEOZONE_TYPE.PLANT] = {
      ...GEOZONE_SHAPE_STYLE[GEOZONE_TYPE.PLANT as keyof GeozoneType],
      zIndex: 3,
    };
    zone[GEOZONE_TYPE.INQUEUE] = {
      ...GEOZONE_SHAPE_STYLE[GEOZONE_TYPE.INQUEUE as keyof GeozoneType],
      zIndex: 2,
    };
    zone[GEOZONE_TYPE.RETURN] = {
      ...GEOZONE_SHAPE_STYLE[GEOZONE_TYPE.RETURN as keyof GeozoneType],
      zIndex: 1,
    };

    return zoneType ? { ...shapeStyles, ...zone[zoneType] } : shapeStyles;
  }

  onPolygonLoad = (polygon: google.maps.Polygon) => {
    this.polygonRef = polygon;
    if (!this.listenersRef) this.listenersRef = [];
    const path = polygon.getPath();
    this.listenersRef.forEach(lis => lis.remove());
    this.listenersRef = [
      path.addListener('set_at', this.onPolygonChanged),
      path.addListener('insert_at', this.onPolygonChanged),
      path.addListener('remove_at', this.onPolygonChanged),
    ];

    if (!this.state.isMapHasBeenBounded) {
      this.setState({ isMapHasBeenBounded: true }, () => this.zoomToBoundsCP());
    }
  };

  zoomToBoundsCP = () => {
    if (!this.map) return;
    const bounds = new google.maps.LatLngBounds();

    const { currentGeozone } = this.props;
    const hasValidGeozone =
      (currentGeozone &&
        currentGeozone?.location &&
        isValidLatLng(currentGeozone.location.latitude, currentGeozone.location.longitude)) ??
      false;
    if (hasValidGeozone) {
      const { latitude, longitude } = currentGeozone!.location!;
      const lat = latitude ?? 0;
      const lng = longitude ?? 0;

      bounds.extend(new google.maps.LatLng(lat - 0.001, lng - 0.001));
      bounds.extend(new google.maps.LatLng(lat + 0.001, lng + 0.001));
    }

    if (this.circleRef) {
      const circleBounds = this.circleRef.getBounds();
      if (circleBounds) {
        bounds.union(circleBounds);
      }
    }

    if (this.polygonRef) {
      this.polygonRef
        .getPath()
        .getArray()
        .forEach(point => {
          bounds.extend(point);
        });
    }

    this.map.fitBounds(bounds);
  };

  generateGeozonePolygon = (shapeStyles: ShapeStyles) => {
    const zone = this.props.currentGeozone?.zone;
    return (
      zone?.polygon &&
      zone?.polygon.points && (
        <Polygon
          path={postGISToGoogleMapsPoints(this.props.currentGeozone?.zone?.polygon?.points) as any}
          onLoad={this.onPolygonLoad}
          editable
          // Event used when dragging the whole Polygon
          onDragEnd={this.onPolygonChanged}
          // Event used when manipulating and adding points
          onMouseUp={this.onPolygonChanged}
          onUnmount={this.onPolygonUnmount}
          options={shapeStyles}
        />
      )
    );
  };

  onRouteMarkerClick = (marker: MarkerType) => () => this.props.onRouteMarkerClick?.(marker);

  onMarkerClose = (marker: MarkerType) => () => this.props.onMarkerClose?.(marker);

  processGeocodeReply = (results: any, status: google.maps.GeocoderStatus) => {
    if (status === google.maps.GeocoderStatus.OK) {
      if (results !== null && results[0]) {
        // Updates the value in the storage
        if (this.props.onPinChanged) {
          this.props.onPinChanged({
            pinMode: false,
            pinDropped: true,
            location: results[0].formatted_address,
            lat: results[0].geometry.location.lat(),
            lng: results[0].geometry.location.lng(),
          });
        }
      }
    }
  };

  onRadiusChanged = () => {
    if (!this.pinCircle) return;
    // Extracts the radius value in miles
    const radius = this.pinCircle.getRadius() / METERS_PER_MILE;
    // Updates the value in the storage
    if (this.props.onPinRadiusChanged) {
      this.props.onPinRadiusChanged(radius.toString());
    }
  };

  /**
   * Handles marker placement from the custom drawing manager
   * @param {google.maps.LatLngLiteral} position The position where the marker was placed
   */
  onMarkerPlaced = (position: google.maps.LatLngLiteral) => {
    // Ensure we don't leave old overlays behind if a pin already exists.
    this.clearPinOverlays();

    // Store the marker position for rendering
    this.pinMarkerPosition = position;

    // Geocode location
    new google.maps.Geocoder().geocode({ location: position }, this.processGeocodeReply);

    // Force re-render to show the marker
    this.forceUpdate();
  };

  /**
   * Handles circle drawing completion from the custom drawing manager
   * @param {Object} circleData The circle data with center and radius
   */
  onCircleDrawingComplete = (circleData: { center: google.maps.LatLngLiteral; radius: number }) => {
    const center: LocationDto = {
      latitude: circleData.center.lat,
      longitude: circleData.center.lng,
    };

    this.onChange({
      circle: {
        center,
        radius: circleData.radius,
      },
      polygon: undefined,
    });
  };

  /**
   * Handles polygon drawing completion from the custom drawing manager
   * @param {Object} polygonData The polygon data with points
   */
  onPolygonDrawingComplete = (polygonData: { points: google.maps.LatLngLiteral[] }) => {
    const points = RouteHistoryTTMap.googleMapsToPostGISPoints(polygonData.points) ?? [];
    this.onChange({
      circle: undefined,
      polygon: {
        points,
      },
    });
  };

  /**
   * Legacy drop pin mode method (kept for compatibility but now uses custom drawing manager)
   * @param {any} mapsMouseEvent mouse map click event with the information of the location
   */
  dropPinMode = (mapsMouseEvent: any) => {
    // Convert to the format expected by the new marker placement handler
    const position = {
      lat: mapsMouseEvent.latLng.lat(),
      lng: mapsMouseEvent.latLng.lng(),
    };

    this.onMarkerPlaced(position);

    // Reset the map cursor
    if (this.map) {
      this.map.setOptions({ draggableCursor: undefined });
    }
  };

  static getRouteHistoryPositionStatus = (position: PositionDto, statuses: StatusEventDto[]) => {
    // if the statuses have only one, returns the position 0
    if (statuses.length === 1) return statuses[0].status;
    // gets the truckStatus
    const truckStatus = statuses.filter((s: StatusEventDto) => s.timestamp === position.timestamp);
    // if the truck status has the exact timestamp returns the status value
    if (truckStatus.length > 0) {
      return truckStatus[0].status;
    }
    // the timestamp can be in a range between all the statuses, loop through all of them to get the status
    for (let i = 0; i < statuses.length; ) {
      if (statuses[i] !== undefined && statuses[i + 1] !== undefined) {
        const currentTime = statuses[i].timestamp as string;
        const nextTime = statuses[i + 1].timestamp as string;
        const positionTime = position.timestamp as string;
        if (currentTime < positionTime && nextTime > positionTime) {
          return statuses[i].status;
        }
      }
      i += 1;
    }

    // the for loop above will not apply a status for the final status in the list, because
    // statuses[i + 1] will always be undefined when statuses[i] is the final status in the list
    // so let's handle the final status below
    if (statuses.length > 0) {
      const lastStatusInList = statuses[statuses.length - 1];
      const lastStatusTime = lastStatusInList.timestamp;

      if (lastStatusTime && position.timestamp && position.timestamp > lastStatusTime) {
        return lastStatusInList.status;
      }
    }

    // Default status if don't find one
    return 'Unknown';
  };

  routeIsSelected = (route: RouteDto) =>
    this.props.selectedRouteHistoryList?.includes(route.id) ?? false;

  routeIsHovered = (route: RouteDto) => this.props.routeHistoryHoverSelection?.id === route.id;

  hoveredRouteOrAllRoutes = (route: RouteDto) => {
    if (this.props.routeHistoryHoverSelection) {
      return this.routeIsHovered(route);
    }

    // no route is hovered, so all routes are returned
    return true;
  };

  renderPositionMarkers = (route: RouteDto, startIdx: number, maxIdx: number) => {
    const positions = route.route;

    // if we need to display more than 500 markers in total, then let's show PNG icons instead of SVG icons to improve performance
    const numTotalMarkers = maxIdx;
    const maxNumOfSvgMarkers = 500;
    const shouldUsePng = numTotalMarkers > maxNumOfSvgMarkers;

    return positions.map((position: PositionDto, idx: number) => {
      const trackedTruckStatuses = route.statuses.filter(
        (s: StatusEventDto) => s.truck?.url === position.trackedObject.url,
      );

      const truckDetails = {
        ...position,
        trackedObject: {
          id: position.trackedObject,
          truckAlias: route.truck.truckAlias,
        },
        status: RouteHistoryTTMap.getRouteHistoryPositionStatus(position, trackedTruckStatuses),
      };

      const positionId = position.id as string;
      const currentRegionId = determineRegionId(this.props.currentRegion as UserRegionDto);
      const popupToggleMap = this.props.popupToggleMap as PopupToggleMap;
      const isMarkerOpenForProductLine = (productLine: string) =>
        isRouteHistoryMarkerOpen(
          popupToggleMap,
          currentRegionId,
          productLine as ProductLine,
          positionId,
        );

      const isOpen = this.props.routeHistoryProductLines?.some(isMarkerOpenForProductLine);

      const { lastMarkerSelected } = popupToggleMap;
      const zIndex =
        startIdx +
        idx +
        RouteHistoryTTMap.calculateZIndex(lastMarkerSelected, positionId, isOpen ?? false, maxIdx);
      const markerData: MarkerDataType = {
        markerId: positionId,
        type: MapMarkerType.Truck,
        driverDetails: position.driver,
        truckDetails,
        ticket: position.ticket!,
        productLine: position.productLine,
      };
      const marker: MarkerType = {
        markerId: positionId,
        type: MapMarkerType.Truck,
        markerData,
      };

      return (
        <PositionMarker
          displayPNG={shouldUsePng}
          parentRouteId={route.id}
          isRouteHistory
          key={positionId}
          zIndex={zIndex}
          mapRef={this.map}
          position={truckDetails}
          onClick={this.onRouteMarkerClick(marker)}
          onCloseClick={this.onMarkerClose(marker)}
          markerData={{
            driverDetails: {
              firstName: route.truck.driverName,
            },
            truckDetails,
            ticket: route.ticket,
            order: route.order,
          }}
          markerStatusMap={retrieveProductLineNameDiff(position.productLine)}
          hideLabel
          isSelected={isOpen}
          canHover
        />
      );
    });
  };

  static renderTableRow = (header: string, value: string | undefined) => {
    const val = value === '' ? undefined : value; // empty strings have no place here
    return (
      <tr>
        <th>{header}</th>
        <td>{val ?? 'N/A'}</td>
      </tr>
    );
  };

  static renderRouteInfoAsTable = (route: RouteDto) => {
    const { order, ticket, truck } = route;
    return (
      <table className={styles.routePopup}>
        <colgroup>
          <col className={styles.labelColumn} />
          <col className={styles.contentColumn} />
        </colgroup>
        <tbody>
          {RouteHistoryTTMap.renderTableRow('Order', order?.orderNumber)}
          {RouteHistoryTTMap.renderTableRow('Ticket', ticket?.ticketNumber)}
          {RouteHistoryTTMap.renderTableRow('Driver', truck?.driverName)}
        </tbody>
      </table>
    );
  };

  static findCenterPosition = (positions: PositionDto[]): PositionDto | null => {
    if (!positions || positions.length === 0) return null;
    const centerIndex = Math.floor(positions.length / 2);
    return positions[centerIndex!];
  };

  renderHoveredRouteInfo = (route: RouteDto) => {
    // exit if this route is not hovered
    if (!this.routeIsHovered(route)) return null;

    // get the "middle" position of the route as an anchor point
    const centerPosition = RouteHistoryTTMap.findCenterPosition(route.route);
    // exit if there aren't enough positions to use as an anchor
    if (centerPosition === null) return null;

    const { latitude, longitude } = centerPosition.location!;
    const position = {
      latitude,
      longitude,
      lat: latitude,
      lng: longitude,
    };

    const icon = {
      path: icons.circlePath,
      fillColor: vars.blue,
      fillOpacity: 0,
      strokeWeight: 0,
      strokeColor: vars.white,
      scale: 1,
    };

    const pixelOffset: google.maps.Size = {
      width: 0,
      height: 0,
      equals: other => other?.width === 0 && other.height === 0,
    };

    return (
      <TTMarker
        key={`marker${0}`}
        mapRef={this.map}
        zIndex={999}
        icon={icon}
        position={position}
        pixelOffset={pixelOffset}
        infoPaneTitle={`Truck #${route.truck.alias}`}
        infoPaneContent={RouteHistoryTTMap.renderRouteInfoAsTable(route)}
        isSelected
        isLabelHover
        isRouteHistory={this.props.isRouteHistory}
        manualInfoPosition
      />
    );
  };

  renderRouteHistory = () => {
    const routes = (this.props.routeHistoryList ?? [])
      .filter(this.routeIsSelected) // discard deselected routes first
      .filter(this.hoveredRouteOrAllRoutes);

    let totalMarkers = 0;
    const routeData: Array<routeDataDto> = [];
    routes.forEach(route => {
      const positionCount = route.route.length;
      const routeDatum: routeDataDto = {
        route,
        markerIdx: totalMarkers,
      };
      routeData.push(routeDatum);
      totalMarkers += positionCount;
    });

    return routeData.map(data => (
      <RouteHistory key={data.route.id}>
        {this.renderHoveredRouteInfo(data.route) /* will only render if a route is hovered */}
        {this.renderPositionMarkers(data.route, data.markerIdx, totalMarkers)}
      </RouteHistory>
    ));
  };

  render() {
    const isPinActive = this.props.pinMap === true ? DROP_PIN_ICON : undefined;
    const onclickMode = this.props.pinMap === true ? this.dropPinMode : undefined;
    const containerStyle = {
      width: '100%',
      height: '100%',
    };
    const definedZoom = this.map?.getZoom() ?? this.defaultZoom;
    const geoFenceStyle = this.getGeofenceStyle(this.props.geofenceAreaType);

    const map = (
      <GoogleMap
        center={this.props.center}
        mapContainerStyle={containerStyle}
        options={{
          zoom: definedZoom,
          streetViewControl: false,
          mapTypeControl: true,
          fullscreenControl: false,
          rotateControl: false,
          controlSize: constants.GM_CONTROLSIZE,
          mapId: process.env.REACT_APP_GOOGLE_MAP_ID || 'DEMO_MAP_ID', // Required for AdvancedMarkerElement
          mapTypeControlOptions: {
            mapTypeIds: [google.maps.MapTypeId.ROADMAP, google.maps.MapTypeId.SATELLITE],
            // style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR, // not compatible with mapId
            position: this.mapPositions.topRight,
          },
          // styles: mapStyles, //
          disableDoubleClickZoom: false,
          draggableCursor: isPinActive,
        }}
        onLoad={this.onLoad}
        onDragEnd={this.handleCenterChange}
        onClick={onclickMode}
      >
        {this.props.isEditable && (
          <CustomDrawingManager
            map={this.map}
            onMarkerComplete={this.onMarkerPlaced}
            onCircleComplete={this.onCircleDrawingComplete}
            onPolygonComplete={this.onPolygonDrawingComplete}
            isEditable={this.props.isEditable}
            showControls
            controlPosition={google.maps.ControlPosition.TOP_LEFT}
          />
        )}

        {this.pinMarkerPosition && (
          <PinMarker map={this.map} position={this.pinMarkerPosition} visible />
        )}

        {this.pinMarkerPosition && (
          <Circle
            center={this.pinMarkerPosition}
            radius={this.getPinRadiusMilesNumber() * METERS_PER_MILE}
            options={{
              strokeColor: vars.orange,
              strokeWeight: 1,
              fillColor: vars.orange,
              fillOpacity: 0.1,
              editable: true,
              draggable: false,
              clickable: false,
            }}
            onLoad={this.onPinCircleLoad}
            onRadiusChanged={this.onRadiusChanged}
            onUnmount={this.onPinCircleUnmount}
          />
        )}

        {this.renderGeozoneCircle(geoFenceStyle)}
        {this.generateGeozonePolygon(geoFenceStyle)}
        {this.renderRouteHistory()}
        {this.renderPlants()}
      </GoogleMap>
    );
    return map;
  }
}

interface RegionWithLocation extends UrlKeyDto {
  location?: LocationDto;
}

export interface MarkerDataType {
  markerId: string;
  type: string;
  driverDetails: any;
  truckDetails?: PositionMarkerDataInfo;
  ticket: UrlKeyDto;
  status?: string;
  productLine?: string;
}

export interface MarkerType {
  markerId: string;
  type: string;
  markerData: OrderDto | MarkerDataType | PlantDto;
}

export function mapStateToProps(state: ReduxState) {
  return {
    routeHistoryHoverSelection: state.routeHistoryHoverSelection,
    pinRadiusMiles: state.pinRadiusMiles,
    plantList: state.plantList,
    popupToggleMap: state.popupToggleMap,
  };
}

type ReduxProps = ReturnType<typeof mapStateToProps>;

export interface OwnProps {
  polygon?: {
    points: LocationDto[];
  };
  currentRegion: RegionWithLocation;
  markers?: PositionMarkerDataInfo[];
  center?: DefaultLocation;
  onCenterChange?: (json: string) => void;
  onRouteMarkerClick?: (marker: MarkerType) => void;
  onMarkerClose?: (marker: MarkerType) => void;
  currentGeozone?: GeoZoneDto;
  geofenceAreaType?: ZoneType;
  isEditable?: boolean;
  onChange?: (evt: MapChangeEvent) => void;
  pinMap?: boolean;
  activePinMap?: PinLocationInfo;
  onPinChanged?: (pinInfo: PinLocationInfo) => void;
  onPinRadiusChanged?: (radius: string) => void;
  isRouteHistory?: boolean;
  routeHistoryProductLines?: string[];
  routeHistoryList: RouteDto[] | null;
  selectedRouteHistoryList?: number[];
}

export interface RouteHistoryTTMapState {
  isFirstMapRender: boolean;
  isMapHasBeenBounded: boolean;
}

export type RouteHistoryTTMapProps = OwnProps & ReduxProps;

export default connect(mapStateToProps)(RouteHistoryTTMap);

export const RouteHistory: React.FunctionComponent<{ children?: React.ReactNode }> = props => (
  <div className="route-history">{props.children}</div>
);
